import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactTable from 'react-table';
import cx from 'classnames';
import moment from 'moment';

import SitePatientApi from '../../../../../api/patient/SitePatientApi';
import StatusChangeSupplierApi from '../../../../../api/patient/StatusChangeSupplierApi';
import Section from '../../../../../common/data-display/Section/Section';
import ModalBoxes from '../../../../../common/feedback/ModalBoxes/ModalBoxes';
import FloatingPanel from '../../../../../common/fullScreenLayout/FloatingPanel';
import Button from '../../../../../common/general/Button';
import NotificationManager from '../../../../../common/notifications/NotificationManager';
import { PATIENT_STATUS_CHANGED } from '../../../../../constants/notificationMessages';
import { CLOSED } from '../../../../../constants/ssuStatuses';
import { MANAGE_SSU_PATIENT_STATUSES } from '../../../../../constants/userOperations';
import { ROLE_SYSTEM_ADMINISTRATOR } from '../../../../../constants/userRoles';
import { userHasAccessTo, userHasRole } from '../../../../../services/auth';
import { isTodayOrBefore } from '../../../../../services/date/validators';
import { onRequestError } from '../../../../../services/handlers';
import {
  NS_COMMON,
  NSC_ENCOUNTER,
  NSC_PATIENT,
  NSPP_PATIENT,
  NSPP_WITHDRAW_REASONS
} from '../../../../../services/i18n/namespaces';
import { getPatientAgeFromDob, getPatientFullName, getPatientPreferredName } from '../../../../../services/patient';
import { CellFormattedDate } from '../../../../CellFormattedDate/CellFormattedDate';
import { PageInfoHeader } from '../../../../PageInfoHeader/PageInfoHeader';
import { useCurrentRoute } from '../../../../root/router';
import { findPatientStatusNameByKey } from '../constants';
import { PatientInfoContext, PatientInfoProvider } from '../PatientInfo/PatientInfoContext';

import PatientStatusChangeEdit from './PatientStatusChangeEdit/PatientStatusChangeEdit';
import {
  addRecordToStatusHistory,
  isTravelOnlyStudy,
  prepareHistory,
  thereAreMoreActiveRecordInsteadSpecified
} from './PatientStatusChangeEdit/PatientStatusChangeEditService';
import PatientStatusRemoveModal from './PatientStatusChangeRemove/PatientStatusRemoveModal';
import PatientStatusProgress from './PatientStatusProgress/PatientStatusProgress';
import { AllowedStatusesResolver } from './AllowedStatusesResolver';
import { dateFormatForShow, statusChangeLocations, statusChangeModes } from './PatientStatusChangeConstants';

import './PatientStatusChange.scss';

function PatientStatusChange() {
  const { t } = useTranslation();
  const { patientInfo, currentStudy, updatePatientRelatedStudies } = useContext(PatientInfoContext);
  const [patientStatusHistory, setPatientStatusHistory] = useState([]);
  const [originalPatientStatusHistory, setOriginalPatientStatusHistory] = useState([]);
  const ssuPatientId = useCurrentRoute().params?.ssuPatientId;
  const [viewMode, setViewMode] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [encounters, setEncounters] = useState([]);
  const [progressTrigger, setProgressTrigger] = useState(true);
  const [missingStatus, setMissingStatus] = useState(null);

  useEffect(() => {
    SitePatientApi.getAvailableStatuses(ssuPatientId).then(({ data: patientStatuses }) => {
      const statuses = patientStatuses.filter(new AllowedStatusesResolver().getAllowedStatuses());
      setStatuses(statuses);
    });
  }, [ssuPatientId, setStatuses]);

  const getPatientStatusHistory = useCallback(() => {
    SitePatientApi.getStatusChangeRecords(ssuPatientId).then(({ data: statusRecords }) => {
      const history = prepareHistory(statusRecords);
      setPatientStatusHistory(history);
      setOriginalPatientStatusHistory(history);
    });
  }, [ssuPatientId, setPatientStatusHistory]);

  useEffect(() => {
    StatusChangeSupplierApi.getCheckedInEncounters(ssuPatientId).then(({ data: encounters }) => {
      setEncounters(encounters);
    });
  }, [ssuPatientId]);

  useEffect(getPatientStatusHistory, [getPatientStatusHistory]);

  const isValidDate = dateToTest => resolveValidator(originalPatientStatusHistory, viewMode)?.(dateToTest);

  const addNewStatusPreview = useCallback(
    draftStatusChangeRecord => {
      setPatientStatusHistory(addRecordToStatusHistory(originalPatientStatusHistory, draftStatusChangeRecord));
    },
    [originalPatientStatusHistory]
  );

  const updatePatientStatusHistoryWithOriginalValues = useCallback(() => {
    setPatientStatusHistory(originalPatientStatusHistory);
  }, [originalPatientStatusHistory]);

  const removeStatus = statusRecordToRemove => {
    const patientStatusRemoveModal = ModalBoxes.open({
      component: (
        <PatientStatusRemoveModal
          patientStatusHistory={originalPatientStatusHistory}
          statusToRemove={statusRecordToRemove}
          removeStatus={(sitePatientHistoryId, deactivationComment) => {
            SitePatientApi.deactivatePatientStatus(ssuPatientId, { sitePatientHistoryId, deactivationComment })
              .then(() => {
                getPatientStatusHistory();
                updatePatientRelatedStudies();
                patientStatusRemoveModal.close();
                setProgressTrigger(!progressTrigger);
              })
              .catch(err => {
                onRequestError(err);
                patientStatusRemoveModal.close();
              });
          }}
        />
      )
    });
  };

  const isAllowedToManagePatientStatuses =
    userHasRole(ROLE_SYSTEM_ADMINISTRATOR) ||
    (currentStudy?.studySiteStatus !== CLOSED && userHasAccessTo(MANAGE_SSU_PATIENT_STATUSES));

  const columns = [
    {
      Header: t(`${NS_COMMON}:date`),
      accessor: 'statusDate',
      width: 140,
      Cell: ({ value }) => <CellFormattedDate date={value} />
    },
    {
      id: 'statusName',
      Header: t(`${NSPP_PATIENT}:patientStatusPage.status`),
      accessor: row => {
        const status = row?.status?.id
          ? t(`${NSPP_PATIENT}:statuses.${findPatientStatusNameByKey(row.status.id)}`)
          : '';
        return row.active === false ? <strike>{status}</strike> : status;
      },
      width: 160
    },
    {
      Header: t(`${NSC_ENCOUNTER}:encounter`),
      accessor: 'encounter',
      width: 200,
      Cell: ({ value }) => value?.name || 'N/A'
    },
    {
      id: 'statusChangeLocation',
      Header: t(`${NSPP_PATIENT}:patientStatusPage.changeLocation`),
      width: 160,
      accessor: row => {
        if (row.statusChangeLocation === 'ITEMGROUP') {
          return row.itemGroup?.name || '';
        } else {
          const statusChangeLocation = statusChangeLocations[row.statusChangeLocation];
          return statusChangeLocation
            ? t(`${NSPP_PATIENT}:patientStatusPage.statusChangeLocations.${statusChangeLocation}`, {
                defaultValue: statusChangeLocation
              })
            : '';
        }
      }
    },
    {
      Header: t(`${NSPP_PATIENT}:patientStatusPage.dateChanged`),
      accessor: 'creationDate',
      width: 120,
      Cell: ({ value }) => <CellFormattedDate date={value} />
    },
    {
      Header: t(`${NSPP_PATIENT}:patientStatusPage.changedBy`),
      accessor: 'changedBy',
      width: 200,
      Cell: ({ value }) => {
        if (value) {
          const fullName = `${value.firstName} ${value.lastName}`;
          return fullName === 'DirectSource Migrations' ? 'System' : fullName;
        }
        return 'N/A';
      }
    },
    {
      Header: t(`${NSPP_PATIENT}:patientStatusPage.reason`),
      minWidth: 235,
      Cell: ({ original: statusChangeRecord }) => {
        return (
          <div className="reason-cell">
            {statusChangeRecord?.reason?.name && (
              <React.Fragment>
                <div className="short-reason">
                  {t(`${statusChangeRecord?.reason.name}`, { ns: NSPP_WITHDRAW_REASONS })}
                  {': '}
                  {statusChangeRecord?.comment}
                </div>
                <div className="full-reason">
                  {t(`${statusChangeRecord?.reason.name}`, { ns: NSPP_WITHDRAW_REASONS })}
                  {': '}
                  {statusChangeRecord?.comment}
                </div>
              </React.Fragment>
            )}
          </div>
        );
      }
    },
    {
      id: 'removeStatus',
      Header: '',
      width: 90,
      accessor: statusRecord => {
        if (
          statusRecord.active &&
          thereAreMoreActiveRecordInsteadSpecified(originalPatientStatusHistory, statusRecord)
        ) {
          return (
            <Button
              disabled={!isAllowedToManagePatientStatuses}
              onClick={() => {
                removeStatus(statusRecord);
              }}
              size="h28"
              priority="low"
            >
              {t('remove')}
            </Button>
          );
        } else {
          return '';
        }
      }
    }
  ];

  const getTrProps = (state, rowInfo) => {
    return { className: cx({ highlight: !rowInfo?.original?.id, 'grey-out': rowInfo?.original.active === false }) };
  };

  const filteredStatuses = useMemo(() => {
    return statuses.filter(status => (viewMode === statusChangeModes.ADD ? status.id !== 'TRANSFERRING' : true));
  }, [statuses, viewMode]);

  return (
    <>
      <PageInfoHeader
        objectRecordLabel={getPatientFullName(patientInfo) + getPatientPreferredName(patientInfo)}
        pageInfo={
          <PageInfoHeader.CollapsibleList>
            <PageInfoHeader.AdditionalInfo title={t(`${NSC_PATIENT}:patientId`)}>
              {patientInfo?.patientId}
            </PageInfoHeader.AdditionalInfo>
            <PageInfoHeader.AdditionalInfo title={t(`${NSC_PATIENT}:subjectId`)}>
              {currentStudy?.patientSubjectId || t(`${NSPP_PATIENT}:info.noSubjectId`)}
            </PageInfoHeader.AdditionalInfo>
            <PageInfoHeader.AdditionalInfo tooltip={t(`${NS_COMMON}:dateOfBirth`)}>
              {patientInfo?.dob?.format(dateFormatForShow)} ({getPatientAgeFromDob(patientInfo)})
            </PageInfoHeader.AdditionalInfo>
            <PageInfoHeader.AdditionalInfo tooltip={t(`${NS_COMMON}:study`)}>
              {currentStudy?.studyName}
            </PageInfoHeader.AdditionalInfo>
            <PageInfoHeader.AdditionalInfo tooltip={t(`${NS_COMMON}:site`)}>
              {currentStudy?.siteName}
            </PageInfoHeader.AdditionalInfo>
          </PageInfoHeader.CollapsibleList>
        }
      />
      <div className="patient-status-change">
        <Section className="info-section">
          <div className="row p-3 m-0 status-bar">
            <strong>
              {`${t('patientStatusPage.currentStatus', { ns: NSPP_PATIENT })}: `}
              {currentStudy?.patientStatus ? t(`statuses.${currentStudy.patientStatus}`, { ns: NSPP_PATIENT }) : '---'}
            </strong>
            <Button
              onClick={() => {
                setViewMode(statusChangeModes.EDIT);
                setMissingStatus(null);
              }}
              size="h28"
              priority="high"
              className="ml-2"
              disabled={!isAllowedToManagePatientStatuses}
            >
              {t('change')}
            </Button>
          </div>
        </Section>
        <Section className="status-progress-section">
          <PatientStatusProgress
            ssuPatientId={ssuPatientId}
            progressTrigger={progressTrigger}
            addMissingStatus={statusName => {
              setMissingStatus(statusName);
              setViewMode(statusChangeModes.ADD);
            }}
            displayAddButton
          />
        </Section>
        <Section className="content-section">
          <div className="px-3 pt-3 section-bar">
            <strong>{t(`${NSPP_PATIENT}:patientStatusPage.statusHistory`)}</strong>
            <Button
              onClick={() => {
                setViewMode(statusChangeModes.ADD);
                setMissingStatus(null);
              }}
              size="h28"
              priority="low"
              className="ml-2 missing-status-btn"
              disabled={!isAllowedToManagePatientStatuses}
            >
              {`+ ${t('patientStatusPage.missingStatus', { ns: NSPP_PATIENT })}`}
            </Button>
          </div>
          <ReactTable
            data={patientStatusHistory}
            columns={columns}
            minRows={1}
            multiSort0
            className="eui-table status-history-table"
            showPagination={false}
            resizable={true}
            sortable={false}
            getTrProps={getTrProps}
          />
        </Section>
        <FloatingPanel
          show={viewMode === statusChangeModes.EDIT || viewMode === statusChangeModes.ADD}
          close={discardChanges}
        >
          <PatientStatusChangeEdit
            viewMode={viewMode}
            statuses={filteredStatuses}
            onStatusRecordChanged={addNewStatusPreview}
            isTravelOnlyStudy={isTravelOnlyStudy(currentStudy)}
            encounters={encounters}
            patientStatusHistory={patientStatusHistory}
            close={discardChanges}
            removeDraftStatuses={updatePatientStatusHistoryWithOriginalValues}
            isValidDate={isValidDate}
            initialStatus={missingStatus}
            updateStatus={(status, reason, date, encounter, comment, nextStatusChangeId) => {
              let updateStatus;
              switch (viewMode) {
                case statusChangeModes.ADD:
                  updateStatus = SitePatientApi.addStatus;
                  break;
                case statusChangeModes.EDIT:
                default:
                  updateStatus = SitePatientApi.changeStatus;
                  break;
              }
              return updateStatus(ssuPatientId, {
                statusCodeId: status?.id,
                withdrawReason: reason?.id,
                comment,
                ssuPatientEncounterId: encounter?.id,
                // prevents date shift timezone issue backend LocalDate ignore zone
                statusDate: date.format('YYYY-MM-DD'),
                statusChangeLocation: 'STATUS_HISTORY',
                nextStatusChangeId
              })
                .then(() => {
                  NotificationManager.success(PATIENT_STATUS_CHANGED);
                  setViewMode(statusChangeModes.VIEW);
                  getPatientStatusHistory();
                  updatePatientRelatedStudies();
                  setProgressTrigger(!progressTrigger);
                  setMissingStatus(null);
                })
                .catch(err => onRequestError(err));
            }}
          />
        </FloatingPanel>
      </div>
    </>
  );

  function discardChanges() {
    setViewMode(statusChangeModes.VIEW);
    updatePatientStatusHistoryWithOriginalValues();
    setMissingStatus(null);
  }
}

export default function PatientStatusChangeComponent(props) {
  return (
    <PatientInfoProvider>
      <PatientStatusChange {...props} />
    </PatientInfoProvider>
  );
}

function resolveValidator(history, statusChangeMode) {
  const validators = {
    [statusChangeModes.EDIT]: date => {
      let start = moment(history.find(s => s.active).statusDate);
      let now = moment();
      return date.isBetween(start, now, 'day', '[]');
    },
    [statusChangeModes.ADD]: isTodayOrBefore
  };
  return validators[statusChangeMode];
}
